import { gql, useMutation } from '@apollo/client';
import Button, { type IButtonProps } from '@orn/shared-web/components/Button/Button';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { usePlaidLink, type PlaidLinkOptions, type PlaidLinkOnSuccessMetadata } from 'react-plaid-link';

import { FINANCIAL_ACCOUNT_FRAGMENT, useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import logger from '../../../lib/logger';
import {
  incrementPeopleProperty,
  trackAccountConnected,
  trackPlaidLinkExited,
  trackPlaidLinkOpened,
} from '../../../lib/mixpanel';
import { ApiErrorCode, extractErrorCode } from '../../../utils/error';
import PlaidConnectionModal from '../PlaidConnectionModal';

import type {
  CreateLinkTokenMutation,
  CreateLinkTokenMutationVariables,
  ExchangePublicTokenMutation,
  ExchangePublicTokenMutationVariables,
  CreateFinancialAccountsMutation,
  CreateFinancialAccountsMutationVariables,
} from './__generated__/PlaidLinkButton.gql';

const CREATE_LINK_TOKEN = gql`
  mutation CreateLinkToken($input: CreateLinkTokenInput!) {
    createLinkToken(input: $input) {
      linkToken
    }
  }
`;

const EXCHANGE_PUBLIC_TOKEN = gql`
  mutation ExchangePublicToken($input: ExchangePublicTokenInput!) {
    exchangePublicToken(input: $input) {
      ...FinancialAccountFields
    }
  }
  ${FINANCIAL_ACCOUNT_FRAGMENT}
`;

const CREATE_FINANCIAL_ACCOUNTS = gql`
  mutation CreateFinancialAccounts($input: CreateFinancialAccountsInput!) {
    createFinancialAccounts(input: $input) {
      ...FinancialAccountFields
    }
  }
  ${FINANCIAL_ACCOUNT_FRAGMENT}
`;

const DISMISS_BANK_CONNECTION_CALLOUT = gql`
  mutation DismissBankConnectionCallout($bankConnectionId: String!) {
    dismissBankConnectionCallout(bankConnectionId: $bankConnectionId)
  }
`;

const getErrorMessage = (errorCode: ApiErrorCode | null | undefined) => {
  if (!errorCode) {
    return null;
  }

  switch (errorCode) {
    case ApiErrorCode.DUPLICATE_ITEM:
      return 'You already have an connection with this institution. Please select a different one.';
    case ApiErrorCode.PLAID_LINK_TOKEN_FAILED:
    case ApiErrorCode.PLAID_EXCHANGE_FAILED:
    default:
      return 'An error occurred while connecting your account. Please try again or contact support if the issue persists.';
  }
};

interface IPlaidLinkButtonProps extends IButtonProps, React.PropsWithChildren {
  onSuccess?: () => void;
  /**
   * Used to skip the bank connection selection modal and go directly to Plaid reconnection.
   * If null, Plaid will create a new bank connection.
   * If non-null and non-undefined, the button will skip the modal and go directly to Plaid reconnection.
   * If undefined, the button will open the modal to select a bank connection.
   */
  bankConnectionId?: string | null;
}

const PlaidLinkButton = ({
  onSuccess,
  children,
  className,
  bankConnectionId,
  ...buttonProps
}: IPlaidLinkButtonProps) => {
  const { financialAccounts } = useFinancialAccounts();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedBankConnectionId, setSelectedBankConnectionId] = React.useState<string | null | undefined>(
    bankConnectionId
  );
  const [shouldTriggerPlaid, setShouldTriggerPlaid] = React.useState(false);

  // If bankConnectionId is provided, skip the modal and go directly to Plaid reconnection
  const shouldSkipModal = bankConnectionId !== undefined;

  const [linkToken, setLinkToken] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState<ApiErrorCode | null>(null);

  const reset = React.useCallback(() => {
    setLinkToken(null);
    setShouldTriggerPlaid(false);
    setSelectedBankConnectionId(undefined);
    setIsLoading(false);
  }, []);

  const openModalWithError = React.useCallback(
    (errorCode: ApiErrorCode) => {
      setErrorCode(errorCode);
      if (!shouldSkipModal) {
        setIsModalOpen(true);
      }
      reset();
    },
    [reset, shouldSkipModal]
  );

  const [createLinkToken] = useMutation<CreateLinkTokenMutation, CreateLinkTokenMutationVariables>(CREATE_LINK_TOKEN, {
    errorPolicy: 'all',
  });

  const [exchangePublicToken] = useMutation<ExchangePublicTokenMutation, ExchangePublicTokenMutationVariables>(
    EXCHANGE_PUBLIC_TOKEN,
    {
      errorPolicy: 'all',
    }
  );

  const [createFinancialAccounts] = useMutation<
    CreateFinancialAccountsMutation,
    CreateFinancialAccountsMutationVariables
  >(CREATE_FINANCIAL_ACCOUNTS, {
    errorPolicy: 'all',
  });

  const [dismissBankConnectionCallout] = useMutation(DISMISS_BANK_CONNECTION_CALLOUT, {
    errorPolicy: 'all',
  });

  const trackNewAccounts = React.useCallback(
    (accounts: ExchangePublicTokenMutation['exchangePublicToken'], isReconnection: boolean) => {
      accounts.forEach((account) => {
        trackAccountConnected({
          accountId: account.id,
          accountType: account.type,
          accountSubtype: account.subtype,
          bankName: account.bank.name,
          bankId: account.bank.id,
          isFirstAccount: financialAccounts.length === 0,
          isReconnection,
        });
      });
      if (!isReconnection) {
        incrementPeopleProperty('account_count', accounts.length);
      }
    },
    [financialAccounts.length]
  );

  const onCreateLinkToken = React.useCallback(
    async (bankConnectionId: string | null | undefined) => {
      try {
        setIsLoading(true);
        setErrorCode(null);

        const response = await createLinkToken({
          variables: {
            input: {
              // Convert null to undefined for GraphQL
              bankConnectionId: bankConnectionId ?? undefined,
            },
          },
        });

        if (response.errors) {
          logger.error('Failed to create link token');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }

        const linkToken = response.data?.createLinkToken?.linkToken;
        if (!linkToken) {
          logger.error('Failed to create link token');
          openModalWithError(ApiErrorCode.PLAID_LINK_TOKEN_FAILED);
          return;
        }

        setLinkToken(linkToken);
      } catch (err) {
        logger.error('Error occurred while creating link token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER);
      }
    },
    [createLinkToken, openModalWithError]
  );

  const onExchangePublicToken = React.useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsLoading(true);
        setErrorCode(null);

        const updateMode = !!selectedBankConnectionId;

        const institutionId = metadata.institution?.institution_id;
        if (!institutionId) {
          logger.error('Missing institution information');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER);
          return;
        }

        // Map Plaid accounts to the required GraphQL input format
        const accounts = metadata.accounts.map((account) => ({
          id: account.id,
          name: account.name,
          mask: account.mask,
          type: account.type,
          subtype: account.subtype,
        }));

        if (!updateMode) {
          const response = await exchangePublicToken({
            variables: {
              input: {
                publicToken,
                institutionId,
                accounts,
              },
            },
          });

          if (response.errors) {
            logger.error('Failed to exchange public token');
            openModalWithError(extractErrorCode(response.errors));
            return;
          }

          if (!response.data?.exchangePublicToken) {
            logger.error('Failed to exchange public token');
            openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
            return;
          }

          trackNewAccounts(response.data.exchangePublicToken, false);
        } else {
          const response = await createFinancialAccounts({
            variables: {
              input: {
                institutionId,
                accounts,
              },
            },
          });

          if (response.errors) {
            logger.error('Failed to create financial accounts');
            openModalWithError(extractErrorCode(response.errors));
            return;
          }

          if (!response.data?.createFinancialAccounts) {
            logger.error('Failed to create financial accounts');
            openModalWithError(ApiErrorCode.INTERNAL_SERVER);
            return;
          }

          trackNewAccounts(response.data.createFinancialAccounts, true);

          // Dismiss the bank connection callout (cache is updated centrally in ApolloProvider)
          if (selectedBankConnectionId) {
            try {
              await dismissBankConnectionCallout({
                variables: { bankConnectionId: selectedBankConnectionId },
              });
            } catch (err) {
              // Log but don't fail the reconnection flow if dismiss fails
              logger.error('Failed to dismiss bank connection callout', err);
            }
          }
        }

        onSuccess?.();
      } catch (err) {
        logger.error('Error occurred while exchanging public token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER);
      } finally {
        reset();
      }
    },
    [
      selectedBankConnectionId,
      onSuccess,
      exchangePublicToken,
      createFinancialAccounts,
      dismissBankConnectionCallout,
      openModalWithError,
      reset,
      trackNewAccounts,
    ]
  );

  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      onExchangePublicToken(publicToken, metadata).catch(() => {
        // Error handling is done in onExchangePublicToken
      });
    },
    onExit: (err) => {
      trackPlaidLinkExited({ hadError: !!err });
      if (err) {
        logger.error('Link was exited with an error', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER);
      }
      reset();
    },
  };

  const { open, ready } = usePlaidLink(config);

  // Auto-open Plaid when link token is ready
  React.useEffect(() => {
    if (linkToken && ready) {
      trackPlaidLinkOpened();
      open();
    }
  }, [linkToken, ready, open]);

  // Trigger Plaid flow when shouldTriggerPlaid changes
  React.useEffect(() => {
    if (shouldTriggerPlaid && !linkToken && !isLoading) {
      onCreateLinkToken(selectedBankConnectionId).catch(() => {
        // Error handling is done in onCreateLinkToken
      });
    }
  }, [shouldTriggerPlaid, linkToken, isLoading, selectedBankConnectionId, onCreateLinkToken]);

  const handleButtonClick = React.useCallback(() => {
    if (shouldSkipModal) {
      // Skip the modal and directly trigger Plaid
      // Restore the original bankConnectionId in case it was reset by a previous error/exit
      setSelectedBankConnectionId(bankConnectionId);
      setShouldTriggerPlaid(true);
    } else {
      setIsModalOpen(true);
    }
  }, [shouldSkipModal, bankConnectionId]);

  const handleModalSelect = React.useCallback((bankConnectionId: string | undefined) => {
    setIsModalOpen(false);
    setSelectedBankConnectionId(bankConnectionId);
    setShouldTriggerPlaid(true);
  }, []);

  const handleModalOpenChange = React.useCallback((nextIsOpen: boolean) => {
    setIsModalOpen(nextIsOpen);
    if (!nextIsOpen) {
      setErrorCode(null);
    }
  }, []);

  return (
    <>
      <div className="flex flex-col items-center">
        {shouldSkipModal && errorCode && (
          <div className="bg-error-secondary text-error-primary mb-6 rounded-lg px-3 py-2 text-sm">
            {getErrorMessage(errorCode)}
          </div>
        )}
        <Button
          className={cx('w-fit', className)}
          onClick={handleButtonClick}
          isLoading={isLoading}
          showTextWhileLoading
          size="sm"
          color="primary"
          {...buttonProps}
        >
          {children}
        </Button>
      </div>
      <PlaidConnectionModal
        isOpen={isModalOpen}
        onOpenChange={handleModalOpenChange}
        onSelect={handleModalSelect}
        errorCode={errorCode}
      />
    </>
  );
};

export default PlaidLinkButton;
